/**
 * Admin-only page for social workflows: preview the same GenAI JSON the site uses,
 * test the **social poster Lambda** (production path), and optional manual Discord /
 * email / browser PNG exports.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import {
  getPicksOfTheDay,
  getParlayOfTheDay,
  postAdminDiscordTest,
  postAdminDiscordPreview,
  postAdminSocialSubscriberEmail,
  postAdminSocialPosterTest,
  getAdminSocialPosterConfig,
  putAdminSocialPosterConfig,
} from '../services/api';
import { formatPickTypeLabel, formatPickValue } from '../utils/formatPickType';
import { useTheme } from '../contexts/ThemeContext';
import html2canvas from 'html2canvas';
import logo from '../logo.png';
import PlayerWithAvatar from '../components/PlayerWithAvatar';
import TeamWithLogo from '../components/TeamWithLogo';
import MatchupCell from '../components/Predictions/MatchupCell';
import ShapRadarChart from '../components/Predictions/ShapRadarChart';

/** Default date: today in Eastern (America/New_York). */
export function easternTodayISO() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

/** Format date for display (e.g. "Sun, Mar 15, 2026"). */
export function formatDateDisplay(isoDate) {
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/New_York',
  });
}

/**
 * Format pick result for display (e.g. "2 TB ✅", "0 hits ❌", live line without win/loss).
 * Only shows ✅/❌ when `hit` is explicitly true/false (game must be final on the API).
 * @param {{ pick_type?: string, actualValue?: number | null, hit?: boolean | null }} pick
 * @returns {string} Empty if no stats yet; live stats without emoji if hit is undecided.
 */
function formatPickResult(pick) {
  if (pick == null) return '';
  const val = pick.actualValue ?? pick.actual_value;
  if (val === undefined || val === null) return '';
  const type = (pick.pick_type || '').toLowerCase().trim();
  const hit = pick.hit;
  const unit = type === 'total_bases' ? 'TB' : type === 'hr' ? 'HR' : 'hits';
  const result = `${Number(val)} ${unit}`;
  if (hit === true) return `${result} ✅`;
  if (hit === false) return `${result} ❌`;
  return `${result} (in progress)`;
}

const BASE_DISCORD_OPTIONS = [
  { value: 'picks_of_the_day', label: 'Picks of the Day' },
  { value: 'parlay_of_the_day', label: 'Parlay of the Day' },
  { value: 'picks_results', label: 'Picks Results' },
  { value: 'parlay_results', label: 'Parlay Results (email-style)' },
  { value: 'parlay_hit', label: 'Parlay CASHED (social Discord template)' },
];

function buildDiscordNotificationOptions(picksOfTheDay) {
  const picks = picksOfTheDay?.picks_of_the_day ?? [];
  const modelInsightOpts = picks.map((pick, idx) => {
    const name = pick?.player_name ?? pick?.playerName ?? `Pick ${idx + 1}`;
    return { value: `model_insight_${idx}`, label: `${name} – Model Insights` };
  });
  return [...BASE_DISCORD_OPTIONS, ...modelInsightOpts];
}

/**
 * Human-readable line for which image the social poster Lambda used (S3 export vs Pillow).
 *
 * @param {Record<string, unknown> | undefined | null} results - `body.results` from poster test response.
 * @returns {string | null}
 */
function formatPickCardSourceLine(results) {
  const pc = results && typeof results === 'object' ? results.pick_card : null;
  if (!pc || typeof pc !== 'object') return null;
  if (pc.ok === true && pc.source === 's3_export' && typeof pc.key === 'string') {
    return `Poster image: S3 export (same as headless /social-export) — ${pc.key}`;
  }
  if (pc.ok === true && pc.source === 'pillow') {
    return 'Poster image: Pillow fallback (no S3 object at social_posts/cards/… for this date).';
  }
  if (pc.ok === false && pc.error != null) {
    return `Poster image: failed — ${String(pc.error)}`;
  }
  return null;
}

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  margin-bottom: 0.5rem;
`;

const PageSubtitle = styled.p`
  font-size: 0.95rem;
  color: ${(p) => p.theme?.secondaryText ?? '#718096'};
  margin-bottom: 1.5rem;
`;

/** Explains production Lambda pipeline vs manual tools on this page. */
const PipelineCallout = styled.aside`
  font-size: 0.88rem;
  line-height: 1.5;
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  background: ${(p) => p.theme?.secondaryBg ?? '#f7fafc'};
  border: 1px solid ${(p) => p.theme?.border ?? '#e2e8f0'};
  border-radius: 0.35rem;
  padding: 1rem 1.1rem;
  margin-bottom: 1.25rem;
  & ul {
    margin: 0.5rem 0 0;
    padding-left: 1.2rem;
  }
  & li {
    margin-bottom: 0.35rem;
  }
  & code {
    font-size: 0.8rem;
    padding: 0.1em 0.35em;
    border-radius: 0.2rem;
    background: ${(p) => p.theme?.cardBg ?? '#fff'};
    border: 1px solid ${(p) => p.theme?.border ?? '#e2e8f0'};
  }
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const DateLabel = styled.label`
  font-size: 0.9rem;
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
`;

const DateInput = styled.input`
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid ${(p) => p.theme?.border ?? '#e2e8f0'};
  font-size: 1rem;
  background: ${(p) => p.theme?.cardBg ?? '#fff'};
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
`;

const TargetSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid ${(p) => p.theme?.border ?? '#e2e8f0'};
  font-size: 0.95rem;
  background: ${(p) => p.theme?.cardBg ?? '#fff'};
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.9rem;
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
`;

const CardSection = styled.section`
  margin-bottom: 2rem;
`;

const SectionHeading = styled.h2`
  font-size: 1.1rem;
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  margin-bottom: 0.75rem;
`;

const SectionHint = styled.p`
  font-size: 0.82rem;
  color: ${(p) => p.theme?.secondaryText ?? '#718096'};
  margin: -0.45rem 0 0.65rem;
  line-height: 1.45;
  & code {
    font-size: 0.78rem;
    padding: 0.08em 0.3em;
    border-radius: 0.2rem;
    background: ${(p) => p.theme?.secondaryBg ?? '#edf2f7'};
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid ${(p) => p.theme?.border ?? '#e2e8f0'};
  background: ${(p) => p.theme?.cardBg ?? '#fff'};
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  &:hover {
    background: ${(p) => p.theme?.secondaryBg ?? '#f7fafc'};
  }
  &.primary {
    background: #4299e1;
    color: white;
    border-color: #4299e1;
    &:hover {
      background: #3182ce;
    }
  }
  &.email {
    background: #48bb78;
    color: #1a202c;
    border-color: #48bb78;
    font-weight: 600;
    &:hover {
      background: #38a169;
    }
  }
`;

/** Card width for admin preview + headless ``/social-export`` screenshots.
 * Wider than the old 320px so compact SHAP bars and labels stay readable on X.
 */
export const CARD_WIDTH_PX = 400;

const CardWrapper = styled.div`
  width: ${CARD_WIDTH_PX}px;
  margin: 0 auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
`;

/** Inner card content - used as target for html2canvas. Dark theme for contrast. */
const SocialCardInner = styled.div`
  width: ${CARD_WIDTH_PX}px;
  min-height: 260px;
  padding: 18px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #1a365d 0%, #2c5282 100%);
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const CardLogoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2px;
`;

const CardLogoImg = styled.img`
  height: 32px;
  width: auto;
  display: block;
  object-fit: contain;
`;

const CardDate = styled.div`
  font-size: 0.8rem;
  opacity: 0.9;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  font-size: 1.1rem;
  margin: 0 0 8px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const CardList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CardListItem = styled.li`
  font-size: 0.875rem;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  line-height: 1.35;
  &:last-child {
    border-bottom: none;
  }
`;

const PickCardBlurb = styled.p`
  font-size: 0.78rem;
  opacity: 0.92;
  margin: 6px 0 0;
  line-height: 1.4;
`;

const ModelInsightsLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  opacity: 0.85;
  margin: 8px 0 4px;
`;

const PickRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const PickAvatar = styled.div`
  flex-shrink: 0;
`;

const PickText = styled.span`
  flex: 1;
  min-width: 0;
`;

const ParlayLegRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ParlayLegAvatar = styled.div`
  flex-shrink: 0;
`;

const ParlayLegText = styled.span`
  flex: 1;
  min-width: 0;
`;

const CardBlurb = styled.p`
  font-size: 0.8rem;
  opacity: 0.95;
  margin: 8px 0 10px;
  line-height: 1.4;
`;

const CardMatchup = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const CardLeg = styled.div`
  font-size: 0.875rem;
  padding: 5px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  &:last-child {
    border-bottom: none;
  }
`;

const CardOdds = styled.div`
  font-size: 0.85rem;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  opacity: 0.95;
`;

const EmptyState = styled.div`
  padding: 18px;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
`;

const CardResultsBanner = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: #48bb78;
  margin-bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const CardRecordPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 800;
  background: rgba(72, 187, 120, 0.25);
  color: #68d391;
  border: 1px solid rgba(72, 187, 120, 0.5);
`;

const CardRecordPillMixed = styled(CardRecordPill)`
  background: rgba(237, 137, 54, 0.2);
  color: #f6ad55;
  border-color: rgba(237, 137, 54, 0.4);
`;

const ResultLine = styled.span`
  font-size: 0.85rem;
  margin-left: 0.25rem;
  font-weight: 500;
  &.won {
    color: #48bb78;
  }
  &.lost {
    color: #fc8181;
  }
  &.pending {
    color: rgba(255, 255, 255, 0.85);
  }
`;

/** Result on its own line so it never wraps (Picks of the Day Results card). */
const ResultBlock = styled(ResultLine)`
  display: block;
  margin-left: 0;
  margin-top: 2px;
  white-space: nowrap;
  &.won {
    font-weight: 600;
    color: #68d391;
  }
  &.pending {
    font-weight: 500;
  }
`;

const WinBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: rgba(72, 187, 120, 0.3);
  color: #68d391;
  margin-right: 6px;
`;

/** Results card inner with optional success accent when all winners */
const ResultsCardInner = styled(SocialCardInner)`
  ${(p) =>
    p.$allWinners
      ? `
    border-top: 3px solid #38a169;
    box-shadow: inset 0 3px 0 0 rgba(72, 187, 120, 0.15);
  `
      : ''}
`;

/** Slot for SHAP chart; dark translucent to match the card and model-page dark mode */
const ShapChartSlot = styled.div`
  background: rgba(45, 55, 72, 0.5);
  border-radius: 6px;
  padding: 6px;
  margin-top: 6px;
  margin-bottom: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  max-width: 100%;
`;

const PickDetailRow = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  & strong {
    margin-right: 0.25rem;
    color: #e2e8f0;
  }
`;

const LineupBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.45rem;
  border-radius: 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  background-color: #48bb78;
  color: #fff;
`;

const LineupMuted = styled.span`
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.8rem;
`;

const ErrorState = styled.div`
  padding: 1rem;
  background: #fed7d7;
  color: #c53030;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const DiscordState = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 0.35rem;
  margin-bottom: 1rem;
  border: 1px solid
    ${(p) => (p.$error ? 'rgba(229, 62, 62, 0.45)' : 'rgba(72, 187, 120, 0.45)')};
  background: ${(p) => (p.$error ? 'rgba(229, 62, 62, 0.1)' : 'rgba(72, 187, 120, 0.1)')};
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  font-size: 0.88rem;
  line-height: 1.45;
`;

const PreviewTextarea = styled.textarea`
  margin: 0;
  padding: 0.85rem 1rem;
  border-radius: 0.35rem;
  border: 1px solid ${(p) => p.theme?.border ?? '#2d3748'};
  background: ${(p) => p.theme?.cardBg ?? '#fff'};
  color: ${(p) => p.theme?.primaryText ?? '#1a202c'};
  font-size: 0.82rem;
  font-family: ui-monospace, monospace;
  line-height: 1.45;
  white-space: pre;
  word-break: break-word;
  max-height: 360px;
  min-height: 200px;
  overflow: auto;
  resize: vertical;
  width: 100%;
  box-sizing: border-box;
`;

export function PicksOfTheDayCard({ dateStr, data, cardRef }) {
  const displayDate = formatDateDisplay(dateStr);
  const picks = data?.picks_of_the_day ?? [];
  const hasPicks = picks.length > 0;

  return (
    <CardWrapper>
      <SocialCardInner ref={cardRef}>
        <CardLogoRow>
          <CardLogoImg src={logo} alt="SlickPicks" />
        </CardLogoRow>
        <CardDate>{displayDate}</CardDate>
        <CardTitle>
          {picks.length === 1 ? 'Pick of the Day' : 'Picks of the Day'}
          <span role="img" aria-label="sparkle">✨</span>
        </CardTitle>
        {hasPicks ? (
          <CardList>
            {picks.map((pick, idx) => {
              const playerName = pick.player_name ?? pick.playerName ?? '—';
              const hasShap = pick?.shap?.feature_names?.length > 0;
              const blurb = pick.blurb || pick.summary_blurb;
              return (
                <CardListItem key={idx}>
                  <PickRow>
                    <PickAvatar>
                      <PlayerWithAvatar
                        playerId={pick.player_id ?? pick.playerId}
                        playerName={playerName}
                        size={32}
                        showName={false}
                      />
                    </PickAvatar>
                    <PickText>
                      <strong>{playerName}</strong>
                      {' — '}
                      {formatPickTypeLabel(pick.pick_type)}
                      {pick.prediction_value != null && (
                        <> ({formatPickValue(pick.pick_type, pick.prediction_value)})</>
                      )}
                    </PickText>
                  </PickRow>
                  {blurb ? <PickCardBlurb>{blurb}</PickCardBlurb> : null}
                  {hasShap ? (
                    <>
                      <ModelInsightsLabel>Model Insights</ModelInsightsLabel>
                      <ShapChartSlot>
                        <ShapRadarChart
                          shapData={pick.shap}
                          playerName={playerName}
                          forceBarLayout
                          compact
                          compactMaxFeatures={6}
                          labelMaxLength={28}
                          maxWidth={CARD_WIDTH_PX - 48}
                        />
                      </ShapChartSlot>
                    </>
                  ) : null}
                </CardListItem>
              );
            })}
          </CardList>
        ) : (
          <EmptyState>No Picks of the Day for this date.</EmptyState>
        )}
      </SocialCardInner>
    </CardWrapper>
  );
}

export function ParlayOfTheDayCard({ dateStr, data, cardRef }) {
  const displayDate = formatDateDisplay(dateStr);
  const hasParlay =
    data?.game_id && Array.isArray(data?.legs) && data.legs.length > 0;

  return (
    <CardWrapper>
      <SocialCardInner ref={cardRef}>
        <CardLogoRow>
          <CardLogoImg src={logo} alt="SlickPicks" />
        </CardLogoRow>
        <CardDate>{displayDate}</CardDate>
        <CardTitle>Parlay of the Day</CardTitle>
        {hasParlay ? (
          <>
            {data.blurb && <CardBlurb>{data.blurb}</CardBlurb>}
            {(data.away_team || data.home_team) && (
              <CardMatchup
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <TeamWithLogo
                  teamId={data.away_team_id}
                  teamName={data.away_team ?? 'Away'}
                  size={20}
                />
                <span style={{ opacity: 0.85 }}>@</span>
                <TeamWithLogo
                  teamId={data.home_team_id}
                  teamName={data.home_team ?? 'Home'}
                  size={20}
                />
              </CardMatchup>
            )}
            {data.legs.map((leg, idx) => (
              <CardLeg key={idx}>
                {leg.type === 'player_prop' ? (
                  <ParlayLegRow>
                    <ParlayLegAvatar>
                      <PlayerWithAvatar
                        playerId={leg.player_id ?? leg.playerId}
                        playerName={leg.player_name}
                        size={32}
                        showName={false}
                      />
                    </ParlayLegAvatar>
                    <ParlayLegText>
                      <strong>{leg.player_name}</strong>
                      {' — '}
                      {formatPickTypeLabel(leg.pick_type)}
                      {leg.prediction_value != null &&
                        ` (${formatPickValue(leg.pick_type, leg.prediction_value)})`}
                      {leg.odds_american != null && (
                        <span style={{ opacity: 0.9 }}>
                          {' '}
                          {leg.odds_american >= 0
                            ? `+${leg.odds_american}`
                            : leg.odds_american}
                        </span>
                      )}
                    </ParlayLegText>
                  </ParlayLegRow>
                ) : (
                  <ParlayLegRow>
                    <ParlayLegAvatar>
                      <TeamWithLogo
                        teamId={leg.team_id}
                        teamName={leg.team ?? leg.description ?? 'Team'}
                        size={28}
                        showName={false}
                      />
                    </ParlayLegAvatar>
                    <ParlayLegText>
                      <strong>{leg.team ?? leg.description ?? 'Moneyline'}</strong>
                    </ParlayLegText>
                  </ParlayLegRow>
                )}
              </CardLeg>
            ))}
            {(data.parlay_odds_american != null || data.to_win_per_10 != null) && (
              <CardOdds>
                {data.parlay_odds_american != null && (
                  <span>
                    Parlay:{' '}
                    {data.parlay_odds_american >= 0
                      ? `+${data.parlay_odds_american}`
                      : data.parlay_odds_american}
                  </span>
                )}
                {data.to_win_per_10 != null && (
                  <span style={{ marginLeft: '1rem' }}>
                    $10 → ${data.to_win_per_10.toFixed(2)}
                  </span>
                )}
              </CardOdds>
            )}
          </>
        ) : (
          <EmptyState>No Parlay of the Day for this date.</EmptyState>
        )}
      </SocialCardInner>
    </CardWrapper>
  );
}

/** Single-pick Model Insights card (one card per pick so we can show more SHAP features). */
function PicksOfTheDayInsightCard({ dateStr, pick, cardRef }) {
  const displayDate = formatDateDisplay(dateStr);
  const playerName = pick?.player_name ?? pick?.playerName ?? '—';
  const hasShap = pick?.shap?.feature_names?.length > 0;
  const teamName = pick?.team_name ?? pick?.teamName;
  const opponent = pick?.opponent;
  const opponentTeamId = pick?.opponent_team_id ?? pick?.opponentTeamId;
  const homeAway = pick?.home_away ?? pick?.homeAway;
  const battingOrder = pick?.batting_order ?? pick?.battingOrder;
  const pitcherName = pick?.pitcher_name ?? pick?.pitcherName;
  const pitcherHand = pick?.pitcher_hand ?? pick?.pitcherHand;

  // Picks of the Day are only chosen when lineup is posted, so never show "Lineup Not Posted".
  const lineupLabel =
    battingOrder > 0 ? `Batting ${battingOrder}` : 'Lineup posted';

  return (
    <CardWrapper>
      <SocialCardInner ref={cardRef}>
        <CardLogoRow>
          <CardLogoImg src={logo} alt="SlickPicks" />
        </CardLogoRow>
        <CardDate>{displayDate}</CardDate>
        <CardTitle>Pick of the Day – Model Insights</CardTitle>
        <PickRow style={{ marginBottom: 8 }}>
          <PickAvatar>
            <PlayerWithAvatar
              playerId={pick?.player_id ?? pick?.playerId}
              playerName={playerName}
              size={32}
              showName={false}
            />
          </PickAvatar>
          <PickText>
            <strong>{playerName}</strong>
            {' — '}
            {formatPickTypeLabel(pick?.pick_type)}
            {pick?.prediction_value != null && (
              <> ({formatPickValue(pick.pick_type, pick.prediction_value)})</>
            )}
          </PickText>
        </PickRow>
        {(teamName || opponent) && (
          <div style={{ marginBottom: 8 }}>
            {teamName != null && (
              <PickDetailRow>
                <strong>Team:</strong>
                <TeamWithLogo
                  teamId={pick?.team_id ?? pick?.teamId}
                  teamName={teamName}
                  size={18}
                />
              </PickDetailRow>
            )}
            {opponent != null && (
              <PickDetailRow>
                <strong>Matchup:</strong>
                <MatchupCell
                  homeAway={homeAway === 'home' ? 'home' : 'away'}
                  opponentTeamId={opponentTeamId}
                  opponent={opponent}
                  size={18}
                />
              </PickDetailRow>
            )}
            {(pitcherName != null || pitcherHand != null) && (
              <PickDetailRow>
                <strong>Pitcher:</strong>
                <PlayerWithAvatar
                  playerId={pick?.pitcher_id ?? pick?.pitcherId}
                  playerName={pitcherName ?? '—'}
                  size={18}
                  showName={true}
                />
                {pitcherHand && (
                  <span style={{ marginLeft: '0.25rem', color: 'rgba(255,255,255,0.8)' }}>
                    ({pitcherHand})
                  </span>
                )}
              </PickDetailRow>
            )}
            <PickDetailRow>
              <strong>Lineup:</strong>
              {battingOrder > 0 ? (
                <LineupBadge>{lineupLabel}</LineupBadge>
              ) : (
                <LineupMuted>{lineupLabel}</LineupMuted>
              )}
            </PickDetailRow>
          </div>
        )}
        {hasShap ? (
          <ShapChartSlot>
            <ShapRadarChart
              shapData={pick.shap}
              playerName={playerName}
              forceBarLayout
              compact
              compactMaxFeatures={6}
            />
          </ShapChartSlot>
        ) : (
          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
            Model insights not available for this pick.
          </div>
        )}
      </SocialCardInner>
    </CardWrapper>
  );
}

function PicksOfTheDayResultsCard({ dateStr, data, cardRef }) {
  const displayDate = formatDateDisplay(dateStr);
  const picks = data?.picks_of_the_day ?? [];
  const hasPicks = picks.length > 0;
  const picksResolved = picks.filter((p) => p.hit === true || p.hit === false);
  const allResolved = hasPicks && picksResolved.length === picks.length;
  const allWinners =
    allResolved &&
    picks.every((p) => p.hit === true);
  const wins = picks.filter((p) => p.hit === true).length;
  const losses = picks.filter((p) => p.hit === false).length;
  const recordStr = allResolved ? `${wins}-${losses}` : null;

  return (
    <CardWrapper>
      <ResultsCardInner ref={cardRef} $allWinners={allWinners}>
        <CardLogoRow>
          <CardLogoImg src={logo} alt="SlickPicks" />
        </CardLogoRow>
        <CardDate>{displayDate}</CardDate>
        <CardTitle>Picks of the Day – Results</CardTitle>
        {allResolved && recordStr != null && (
          <CardResultsBanner>
            {allWinners ? (
              <>
                <span role="img" aria-label="trophy">🏆</span>
                <span>All winners</span>
                <CardRecordPill>{recordStr}</CardRecordPill>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, opacity: 0.95, textTransform: 'none' }}>
                  Clean sweep.
                </span>
              </>
            ) : (
              <>
                <CardRecordPillMixed>{recordStr}</CardRecordPillMixed>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)', textTransform: 'none' }}>
                  {wins} hit{wins !== 1 ? 's' : ''} today.
                </span>
              </>
            )}
          </CardResultsBanner>
        )}
        {hasPicks ? (
          <CardList>
            {picks.map((pick, idx) => {
              const resultStr = formatPickResult(pick);
              const hit = pick.hit === true;
              const missed = pick.hit === false;
              return (
                <CardListItem key={idx}>
                  <PickRow>
                    <PickAvatar>
                      <PlayerWithAvatar
                        playerId={pick.player_id ?? pick.playerId}
                        playerName={pick.player_name ?? pick.playerName}
                        size={32}
                        showName={false}
                      />
                    </PickAvatar>
                    <PickText>
                      <strong>{pick.player_name ?? pick.playerName ?? '—'}</strong>
                      {' — '}
                      {formatPickTypeLabel(pick.pick_type)}
                      {pick.prediction_value != null && (
                        <> ({formatPickValue(pick.pick_type, pick.prediction_value)})</>
                      )}
                      {resultStr ? (
                        <ResultBlock className={hit ? 'won' : missed ? 'lost' : 'pending'}>
                          {hit && <WinBadge>Hit</WinBadge>}
                          Result: {resultStr}
                        </ResultBlock>
                      ) : (
                        <ResultBlock style={{ opacity: 0.8 }}>Result: —</ResultBlock>
                      )}
                    </PickText>
                  </PickRow>
                </CardListItem>
              );
            })}
          </CardList>
        ) : (
          <EmptyState>No Picks of the Day for this date.</EmptyState>
        )}
      </ResultsCardInner>
    </CardWrapper>
  );
}

function ParlayOfTheDayResultsCard({ dateStr, data, cardRef }) {
  const displayDate = formatDateDisplay(dateStr);
  const hasParlay =
    data?.game_id && Array.isArray(data?.legs) && data.legs.length > 0;
  const legs = data?.legs ?? [];
  const legsResolved = legs.filter((l) => l.hit === true || l.hit === false);
  const allResolved = hasParlay && legsResolved.length === legs.length;
  const parlayHit = allResolved && legs.every((l) => l.hit === true);

  return (
    <CardWrapper>
      <SocialCardInner ref={cardRef}>
        <CardLogoRow>
          <CardLogoImg src={logo} alt="SlickPicks" />
        </CardLogoRow>
        <CardDate>{displayDate}</CardDate>
        <CardTitle>Parlay of the Day – Results</CardTitle>
        {parlayHit && (
          <CardResultsBanner>
            <span role="img" aria-label="check">✅</span>
            Parlay hit!
          </CardResultsBanner>
        )}
        {hasParlay ? (
          <>
            {(data.away_team || data.home_team) && (
              <CardMatchup
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <TeamWithLogo
                  teamId={data.away_team_id}
                  teamName={data.away_team ?? 'Away'}
                  size={20}
                />
                <span style={{ opacity: 0.85 }}>@</span>
                <TeamWithLogo
                  teamId={data.home_team_id}
                  teamName={data.home_team ?? 'Home'}
                  size={20}
                />
              </CardMatchup>
            )}
            {legs.map((leg, idx) => {
              const isPlayerProp = leg.type === 'player_prop';
              const resultStr = isPlayerProp
                ? formatPickResult(leg)
                : leg.hit === true
                  ? 'Won ✅'
                  : leg.hit === false
                    ? 'Lost ❌'
                    : '—';
              const hit = leg.hit === true;
              const lost = leg.hit === false;
              return (
                <CardLeg key={idx}>
                  {isPlayerProp ? (
                    <ParlayLegRow>
                      <ParlayLegAvatar>
                        <PlayerWithAvatar
                          playerId={leg.player_id ?? leg.playerId}
                          playerName={leg.player_name}
                          size={32}
                          showName={false}
                        />
                      </ParlayLegAvatar>
                      <ParlayLegText>
                        <strong>{leg.player_name}</strong>
                        {' — '}
                        {formatPickTypeLabel(leg.pick_type)}
                        {leg.prediction_value != null &&
                          ` (${formatPickValue(leg.pick_type, leg.prediction_value)})`}
                        {' · Result: '}
                        <ResultLine
                          className={
                            hit ? 'won' : lost ? 'lost' : isPlayerProp && resultStr ? 'pending' : ''
                          }
                        >
                          {resultStr || '—'}
                        </ResultLine>
                      </ParlayLegText>
                    </ParlayLegRow>
                  ) : (
                    <ParlayLegRow>
                      <ParlayLegAvatar>
                        <TeamWithLogo
                          teamId={leg.team_id}
                          teamName={leg.team ?? leg.description ?? 'Team'}
                          size={28}
                          showName={false}
                        />
                      </ParlayLegAvatar>
                      <ParlayLegText>
                        <strong>{leg.team ?? leg.description ?? 'Moneyline'}</strong>
                        {' · Result: '}
                        <ResultLine className={hit ? 'won' : lost ? 'lost' : ''}>
                          {resultStr}
                        </ResultLine>
                      </ParlayLegText>
                    </ParlayLegRow>
                  )}
                </CardLeg>
              );
            })}
          </>
        ) : (
          <EmptyState>No Parlay of the Day for this date.</EmptyState>
        )}
      </SocialCardInner>
    </CardWrapper>
  );
}

function AdminSocialCardsPage() {
  const { theme } = useTheme();
  const [dateStr, setDateStr] = useState(easternTodayISO());
  const [discordTarget, setDiscordTarget] = useState('test');
  const [customWebhookUrl, setCustomWebhookUrl] = useState('');
  const [dispatchType, setDispatchType] = useState('picks_of_the_day');
  /** MLB game id for ``parlay_hit`` template + social poster test (must match a parlay row). */
  const [parlayGameId, setParlayGameId] = useState('');
  const [includeImage, setIncludeImage] = useState(true);
  const [picksOfTheDay, setPicksOfTheDay] = useState(null);
  /** Enriched parlay objects from API (same order as ``parlays`` in S3). */
  const [parlayList, setParlayList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingDiscord, setSendingDiscord] = useState(null);
  const [discordResult, setDiscordResult] = useState(null);
  const [discordError, setDiscordError] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [emailResult, setEmailResult] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [sendingSocialPosterTest, setSendingSocialPosterTest] = useState(false);
  const [socialPosterTestResult, setSocialPosterTestResult] = useState(null);
  const [socialPosterTestError, setSocialPosterTestError] = useState(null);
  const [socialPosterMode, setSocialPosterMode] = useState(null); // null = loading
  const [socialPosterModeEdit, setSocialPosterModeEdit] = useState('off');
  const [savingMode, setSavingMode] = useState(false);
  const [saveModeResult, setSaveModeResult] = useState(null);
  const [saveModeError, setSaveModeError] = useState(null);
  /** Who receives the social preview email: all subscribers (by list) or admins only. */
  const [emailAudience, setEmailAudience] = useState('admins');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editablePayloadJson, setEditablePayloadJson] = useState('');
  const picksCardRef = useRef(null);
  const primaryParlayCardRef = useRef(null);
  const parlayCardRefsArray = useRef([]);
  const picksInsightsCardRefs = useRef([]);
  const picksResultsCardRef = useRef(null);
  const primaryParlayResultsCardRef = useRef(null);
  const parlayResultsCardRefsArray = useRef([]);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      getPicksOfTheDay(dateStr).catch(() => ({ picks_of_the_day: [] })),
      getParlayOfTheDay(dateStr).catch(() => ({ date: dateStr, parlays: [] })),
    ])
      .then(([picks, parlayBody]) => {
        setPicksOfTheDay(picks);
        const raw = parlayBody?.parlays;
        let list = [];
        if (Array.isArray(raw)) {
          list = raw.filter(
            (p) => p?.game_id && Array.isArray(p?.legs) && p.legs.length > 0,
          );
        } else if (
          parlayBody?.game_id &&
          Array.isArray(parlayBody?.legs) &&
          parlayBody.legs.length > 0
        ) {
          list = [parlayBody];
        }
        setParlayList(list);
      })
      .catch((err) => {
        setError(err?.message ?? 'Failed to load data');
        setPicksOfTheDay(null);
        setParlayList([]);
      })
      .finally(() => setLoading(false));
  }, [dateStr]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (dispatchType !== 'parlay_hit') return;
    const ids = parlayList
      .map((p) => (p?.game_id != null ? String(p.game_id).trim() : ''))
      .filter(Boolean);
    setParlayGameId((prev) => {
      if (prev && ids.includes(prev)) return prev;
      return ids[0] || '';
    });
  }, [dispatchType, parlayList]);

  useEffect(() => {
    getAdminSocialPosterConfig()
      .then((cfg) => {
        setSocialPosterMode(cfg.mode ?? 'off');
        setSocialPosterModeEdit(cfg.mode ?? 'off');
      })
      .catch(() => {
        setSocialPosterMode('off');
        setSocialPosterModeEdit('off');
      });
  }, []);

  const saveMode = useCallback(async () => {
    setSavingMode(true);
    setSaveModeResult(null);
    setSaveModeError(null);
    try {
      const result = await putAdminSocialPosterConfig({ mode: socialPosterModeEdit });
      setSocialPosterMode(result.mode);
      setSocialPosterModeEdit(result.mode);
      setSaveModeResult(result);
    } catch (err) {
      setSaveModeError(err.response?.data?.error || err.message || 'Failed to save mode');
    } finally {
      setSavingMode(false);
    }
  }, [socialPosterModeEdit]);

  const getDispatchConfig = useCallback(
    (notificationType) => {
      if (notificationType === 'picks_of_the_day') {
        return {
          payloadData: picksOfTheDay,
          ref: picksCardRef,
          imageFilename: `slickpicks-picks-${dateStr}.png`,
        };
      }
      if (notificationType === 'parlay_of_the_day') {
        return {
          payloadData:
            parlayList.length > 0
              ? { date: dateStr, parlays: parlayList }
              : null,
          ref: primaryParlayCardRef,
          imageFilename: `slickpicks-parlay-${dateStr}.png`,
        };
      }
      if (notificationType === 'picks_results') {
        return {
          payloadData: picksOfTheDay,
          ref: picksResultsCardRef,
          imageFilename: `slickpicks-picks-results-${dateStr}.png`,
        };
      }
      if (notificationType === 'parlay_results' || notificationType === 'parlay_hit') {
        return {
          payloadData:
            parlayList.length > 0
              ? { date: dateStr, parlays: parlayList }
              : null,
          ref: primaryParlayResultsCardRef,
          imageFilename: `slickpicks-parlay-results-${dateStr}.png`,
        };
      }
      const modelMatch = (notificationType || '').match(/^model_insight_(\d+)$/);
      if (modelMatch) {
        const idx = parseInt(modelMatch[1], 10);
        const picks = picksOfTheDay?.picks_of_the_day ?? [];
        const pick = picks[idx];
        const el = picksInsightsCardRefs.current?.[idx];
        const name = pick?.player_name ?? pick?.playerName ?? 'pick';
        const slug = name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
        return {
          payloadData: pick,
          ref: el ? { current: el } : null,
          imageFilename: `slickpicks-picks-insight-${dateStr}-${slug}.png`,
        };
      }
      return { payloadData: null, ref: null, imageFilename: '' };
    },
    [dateStr, parlayList, picksOfTheDay]
  );

  const assignParlayCardRef = useCallback(
    (index) => (element) => {
      parlayCardRefsArray.current[index] = element;
      if (index === 0) {
        primaryParlayCardRef.current = element;
      }
    },
    []
  );

  const assignParlayResultsCardRef = useCallback(
    (index) => (element) => {
      parlayResultsCardRefsArray.current[index] = element;
      if (index === 0) {
        primaryParlayResultsCardRef.current = element;
      }
    },
    []
  );

  const loadPreview = useCallback(async () => {
    const { payloadData, imageFilename } = getDispatchConfig(dispatchType);
    const isModelInsight = (dispatchType || '').startsWith('model_insight_');

    if (isModelInsight) {
      if (!payloadData) {
        setEditablePayloadJson('');
        return;
      }
      const playerName = payloadData?.player_name ?? payloadData?.playerName ?? 'Pick';
      const pickType = formatPickTypeLabel(payloadData?.pick_type);
      const pred = payloadData?.prediction_value ?? payloadData?.predictionValue;
      const formattedValue = formatPickValue(payloadData?.pick_type, pred);
      const displayDate = formatDateDisplay(dateStr);
      const content = `**Pick of the Day – Model Insights — ${displayDate}**\n${playerName} — ${pickType}${formattedValue ? ` (${formattedValue})` : ''}\n\n[slickpicks.bet](https://slickpicks.bet)`;
      const payload = includeImage
        ? { content, embeds: [{ image: { url: `attachment://${imageFilename}` } }] }
        : { content };
      setEditablePayloadJson(JSON.stringify(payload, null, 2));
      return;
    }

    if (!payloadData) {
      setEditablePayloadJson('');
      return;
    }
    setPreviewLoading(true);
    setDiscordError(null);
    try {
      const preview = await postAdminDiscordPreview({
        notification_type: dispatchType,
        date: dateStr,
        data: payloadData,
        include_image: includeImage,
        image_filename: imageFilename,
        ...(dispatchType === 'parlay_hit' && parlayGameId ? { game_id: parlayGameId } : {}),
      });
      setEditablePayloadJson(
        JSON.stringify(preview.payload_preview, null, 2)
      );
    } catch (err) {
      setDiscordError(err.response?.data?.error || err.message || 'Failed to preview Discord payload');
      setEditablePayloadJson('');
    } finally {
      setPreviewLoading(false);
    }
  }, [dateStr, dispatchType, getDispatchConfig, includeImage, parlayGameId]);

  useEffect(() => {
    if (loading) return;
    loadPreview();
  }, [loading, loadPreview]);

  useEffect(() => {
    const opts = buildDiscordNotificationOptions(picksOfTheDay);
    const validValues = new Set(opts.map((o) => o.value));
    if (dispatchType && !validValues.has(dispatchType)) {
      setDispatchType(opts[0]?.value ?? 'picks_of_the_day');
    }
  }, [picksOfTheDay, dispatchType]);

  const captureAndDownload = useCallback(
    (ref, filename) => {
      if (!ref?.current) return;
      const scale = 2;
      html2canvas(ref.current, {
        scale,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      }).then((canvas) => {
        const link = document.createElement('a');
        link.download = filename;
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    },
    []
  );

  const captureCardDataUrl = useCallback(async (ref) => {
    if (!ref?.current) throw new Error('Card is not ready yet');
    const canvas = await html2canvas(ref.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: null,
      logging: false,
    });
    return canvas.toDataURL('image/png');
  }, []);

  const sendSelectedToDiscord = useCallback(async () => {
    const { payloadData, ref, imageFilename } = getDispatchConfig(dispatchType);
    if (!payloadData) {
      setDiscordError('No source data for selected type/date. Choose another type or date.');
      setDiscordResult(null);
      return;
    }
    if (includeImage && !ref?.current) {
      setDiscordError('Card image not ready. Scroll to the card and try again.');
      setDiscordResult(null);
      return;
    }

    setSendingDiscord(dispatchType);
    setDiscordError(null);
    setDiscordResult(null);
    try {
      let imageDataUrl = '';
      if (includeImage) {
        imageDataUrl = await captureCardDataUrl(ref);
      }
      let rawPayload = null;
      if (editablePayloadJson.trim()) {
        try {
          rawPayload = JSON.parse(editablePayloadJson);
        } catch {
          setDiscordError('Payload JSON is invalid. Fix the preview or click Refresh.');
          setSendingDiscord(null);
          return;
        }
      }
      const result = await postAdminDiscordTest({
        target: discordTarget,
        custom_webhook_url: discordTarget === 'custom' ? customWebhookUrl.trim() : '',
        ...(rawPayload
          ? { raw_payload: rawPayload }
          : {
              use_production_template: true,
              notification_type: dispatchType,
              date: dateStr,
              data: payloadData,
              ...(dispatchType === 'parlay_hit' && parlayGameId ? { game_id: parlayGameId } : {}),
            }),
        image_data_url: imageDataUrl,
        image_filename: includeImage ? imageFilename : '',
      });
      setDiscordResult(result);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to send Discord post';
      setDiscordError(msg);
    } finally {
      setSendingDiscord(null);
    }
  }, [
    captureCardDataUrl,
    customWebhookUrl,
    dateStr,
    discordTarget,
    dispatchType,
    editablePayloadJson,
    getDispatchConfig,
    includeImage,
    parlayGameId,
  ]);

  const subscriptionForDispatch = useCallback(() => {
    if (
      dispatchType === 'picks_results' ||
      dispatchType === 'parlay_results' ||
      dispatchType === 'parlay_hit'
    ) {
      return 'results';
    }
    return 'picks';
  }, [dispatchType]);

  const sendSelectedToSubscribersEmail = useCallback(async () => {
    const { payloadData, ref, imageFilename } = getDispatchConfig(dispatchType);
    if (!payloadData) {
      setEmailError('No source data for selected type/date. Choose another type or date.');
      setEmailResult(null);
      return;
    }
    if (includeImage && !ref?.current) {
      setEmailError('Card image not ready. Scroll to the card and try again.');
      setEmailResult(null);
      return;
    }

    setSendingEmail(dispatchType);
    setEmailError(null);
    setEmailResult(null);
    try {
      let imageDataUrl = '';
      if (includeImage) {
        imageDataUrl = await captureCardDataUrl(ref);
      }
      const subscription = subscriptionForDispatch();

      if (dispatchType === 'picks_of_the_day') {
        const picks = payloadData?.picks_of_the_day ?? [];
        if (!Array.isArray(picks) || picks.length === 0) {
          setEmailError('No Picks of the Day in payload for this date.');
          setSendingEmail(null);
          return;
        }
        const structuredResult = await postAdminSocialSubscriberEmail({
          subscription,
          audience: emailAudience,
          date: dateStr,
          email_format: 'picks_social_with_insights',
          data: payloadData,
          image_data_url: imageDataUrl,
          image_filename: includeImage ? imageFilename : '',
        });
        setEmailResult(structuredResult);
        return;
      }

      let rawPayload = null;
      if (editablePayloadJson.trim()) {
        try {
          rawPayload = JSON.parse(editablePayloadJson);
        } catch {
          setEmailError('Payload JSON is invalid. Fix the preview or click Refresh.');
          setSendingEmail(null);
          return;
        }
      }
      const isTemplate =
        !rawPayload &&
        (dispatchType === 'parlay_of_the_day' ||
          dispatchType === 'picks_results' ||
          dispatchType === 'parlay_results' ||
          dispatchType === 'parlay_hit');

      const body = {
        subscription,
        audience: emailAudience,
        date: dateStr,
        image_data_url: imageDataUrl,
        image_filename: includeImage ? imageFilename : '',
        ...(rawPayload
          ? { raw_payload: rawPayload }
          : isTemplate
            ? {
                use_production_template: true,
                notification_type: dispatchType,
                date: dateStr,
                data: payloadData,
                ...(dispatchType === 'parlay_hit' && parlayGameId
                  ? { game_id: parlayGameId }
                  : {}),
              }
            : {}),
      };

      if (!rawPayload && !isTemplate) {
        setEmailError(
          'No payload JSON. Click “Refresh from template” or paste a valid Discord payload.',
        );
        setSendingEmail(null);
        return;
      }

      const result = await postAdminSocialSubscriberEmail(body);
      setEmailResult(result);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to send subscriber email';
      setEmailError(msg);
    } finally {
      setSendingEmail(null);
    }
  }, [
    captureCardDataUrl,
    dateStr,
    dispatchType,
    editablePayloadJson,
    getDispatchConfig,
    includeImage,
    subscriptionForDispatch,
    emailAudience,
    parlayGameId,
  ]);

  const triggerSocialPosterTest = useCallback(async () => {
    const posterTypes = [
      'picks_of_the_day',
      'parlay_of_the_day',
      'parlay_hit',
      'picks_results',
    ];
    const notificationType = posterTypes.includes(dispatchType) ? dispatchType : 'picks_of_the_day';
    setSendingSocialPosterTest(true);
    setSocialPosterTestResult(null);
    setSocialPosterTestError(null);
    try {
      const body = { notification_type: notificationType, date: dateStr };
      if (notificationType === 'parlay_hit' && parlayGameId) {
        body.game_id = parlayGameId;
      }
      const result = await postAdminSocialPosterTest(body);
      setSocialPosterTestResult(result);
    } catch (err) {
      setSocialPosterTestError(err.response?.data?.error || err.message || 'Failed to invoke social poster Lambda');
    } finally {
      setSendingSocialPosterTest(false);
    }
  }, [dispatchType, dateStr, parlayGameId]);

  const downloadPicks = () =>
    captureAndDownload(
      picksCardRef,
      `slickpicks-picks-${dateStr}.png`
    );
  const downloadParlayAt = useCallback(
    (idx) => {
      const el = parlayCardRefsArray.current[idx];
      if (!el) return;
      const gid = String(parlayList[idx]?.game_id ?? idx).replace(
        /[^a-zA-Z0-9_-]/g,
        '',
      );
      captureAndDownload(
        { current: el },
        `slickpicks-parlay-${dateStr}-${gid}.png`,
      );
    },
    [captureAndDownload, dateStr, parlayList],
  );
  const downloadPicksInsight = (index, playerName) => {
    const el = picksInsightsCardRefs.current?.[index];
    if (!el) return;
    const slug = (playerName || 'pick')
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9-]/g, '');
    captureAndDownload(
      { current: el },
      `slickpicks-picks-insight-${dateStr}-${slug}.png`,
    );
  };

  const downloadPicksResults = () =>
    captureAndDownload(
      picksResultsCardRef,
      `slickpicks-picks-results-${dateStr}.png`
    );
  const downloadParlayResultsAt = useCallback(
    (idx) => {
      const el = parlayResultsCardRefsArray.current[idx];
      if (!el) return;
      const gid = String(parlayList[idx]?.game_id ?? idx).replace(
        /[^a-zA-Z0-9_-]/g,
        '',
      );
      captureAndDownload(
        { current: el },
        `slickpicks-parlay-results-${dateStr}-${gid}.png`,
      );
    },
    [captureAndDownload, dateStr, parlayList],
  );

  return (
    <PageContainer>
      <PageTitle theme={theme}>Social Media Mgmt</PageTitle>
      <PipelineCallout theme={theme}>
        <strong>Production pipeline (source of truth for automated posts)</strong>
        <ul>
          <li>
            <strong>GenAI Lambda</strong> writes picks JSON to S3, e.g.{' '}
            <code>genai_picks/picks_of_the_day_YYYY-MM-DD.json</code> and{' '}
            <code>genai_picks/parlay_of_the_day_YYYY-MM-DD.json</code>.
          </li>
          <li>
            <strong>Social poster Lambda</strong> (mode below) reads that JSON and posts to Discord /
            X / Instagram / TikTok when enabled. For the <strong>image</strong>, it{' '}
            <strong>prefers</strong> a PNG already in S3 at{' '}
            <code>social_posts/cards/pick_type_date.png</code> (from the{' '}
            <strong>social card renderer</strong> Lambda opening the public{' '}
            <code>/social-export</code> route). If that file is missing, the poster generates a card
            with <strong>Pillow</strong> instead — similar layout, not pixel-identical to React.
          </li>
          <li>
            <strong>Test social poster</strong> invokes the same Lambda in test mode (test Discord
            only). The line it prints after success shows whether the run used the S3 export or
            Pillow.
          </li>
          <li>
            <strong>Manual only:</strong> “Save PNG” in the browser (html2canvas), sending Discord /
            email with an attached screenshot, and <strong>model insight / results</strong> cards are
            not what the automated daily poster uses unless you extend the pipeline.
          </li>
        </ul>
      </PipelineCallout>
      <PageSubtitle theme={theme}>
        Pick a date (Eastern). Card previews use the same API data as the live site. Use{' '}
        <strong>Test social poster</strong> to verify the Lambda; use Discord / email when you want a
        manual send with an optional browser-captured image.
      </PageSubtitle>

      <DateRow>
        <DateLabel theme={theme}>Date (Eastern)</DateLabel>
        <DateInput
          type="date"
          value={dateStr}
          onChange={(e) => setDateStr(e.target.value)}
          theme={theme}
        />
        <DateLabel theme={theme}>Discord target</DateLabel>
        <TargetSelect
          theme={theme}
          value={discordTarget}
          onChange={(e) => setDiscordTarget(e.target.value)}
        >
          <option value="test">Test webhook</option>
          <option value="public">Public webhook</option>
          <option value="custom">Custom webhook</option>
        </TargetSelect>
      </DateRow>

      {discordTarget === 'custom' && (
        <DateRow style={{ marginTop: '-0.75rem' }}>
          <DateLabel theme={theme}>Custom webhook URL</DateLabel>
          <DateInput
            type="text"
            value={customWebhookUrl}
            onChange={(e) => setCustomWebhookUrl(e.target.value)}
            theme={theme}
            style={{ minWidth: '420px' }}
            placeholder="https://discord.com/api/webhooks/..."
          />
        </DateRow>
      )}

      {error && <ErrorState>{error}</ErrorState>}
      {discordResult && (
        <DiscordState theme={theme}>
          Sent to Discord ({discordResult.target}) with status {discordResult.status_code}.
        </DiscordState>
      )}
      {discordError && (
        <DiscordState theme={theme} $error>
          {discordError}
        </DiscordState>
      )}
      {emailResult && (
        <DiscordState theme={theme}>
          Sent email ({emailResult.audience || 'subscribers'}
          {emailResult.audience === 'subscribers'
            ? `, ${emailResult.subscription} list`
            : ''}
          {emailResult.email_format === 'picks_social_with_insights' ? ', picks+SHAP layout' : ''}):{' '}
          {emailResult.emails_sent} delivered — {emailResult.subject}
        </DiscordState>
      )}
      {emailError && (
        <DiscordState theme={theme} $error>
          {emailError}
        </DiscordState>
      )}
      {socialPosterTestResult && (
        <DiscordState theme={theme} $error={!socialPosterTestResult.ok}>
          {socialPosterTestResult.ok ? (
            <>
              Social poster test sent via <strong>{socialPosterTestResult.invoke || 'lambda'}</strong>{' '}
              ({socialPosterTestResult.pick_type} · {socialPosterTestResult.date}) — check your test
              Discord channel.
              {formatPickCardSourceLine(socialPosterTestResult.results) && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.9rem', fontWeight: 500 }}>
                  {formatPickCardSourceLine(socialPosterTestResult.results)}
                </div>
              )}
            </>
          ) : (
            <div>
              <strong>Social poster test did not succeed.</strong>
              {socialPosterTestResult.error && (
                <div style={{ marginTop: '0.35rem' }}>{socialPosterTestResult.error}</div>
              )}
              {socialPosterTestResult.handler_error && (
                <div style={{ marginTop: '0.35rem' }}>Handler: {socialPosterTestResult.handler_error}</div>
              )}
              {socialPosterTestResult.hint && (
                <div style={{ marginTop: '0.35rem', fontSize: '0.9rem', opacity: 0.95 }}>
                  {socialPosterTestResult.hint}
                </div>
              )}
              {Object.keys(socialPosterTestResult.results || {}).length > 0 && (
                <pre
                  style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: '12rem',
                    marginBottom: 0,
                  }}
                >
                  {JSON.stringify(socialPosterTestResult.results, null, 2)}
                </pre>
              )}
            </div>
          )}
        </DiscordState>
      )}
      {socialPosterTestError && (
        <DiscordState theme={theme} $error>
          Social poster test error: {socialPosterTestError}
        </DiscordState>
      )}

      <CardSection>
        <SectionHeading theme={theme}>Social Poster</SectionHeading>
        <PageSubtitle theme={theme} style={{ marginBottom: '0.75rem' }}>
          Stored in S3 (<code>social_poster/config.json</code>). When set to production or testing,
          GenAI invokes this Lambda after writing picks JSON. Changes apply immediately — no Lambda
          redeploy.
        </PageSubtitle>
        <ActionsRow style={{ alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '0.85rem',
            fontWeight: 600,
            color: socialPosterMode === 'production'
              ? '#22c55e'
              : socialPosterMode === 'testing'
                ? '#f59e0b'
                : socialPosterMode === 'off'
                  ? (theme?.secondaryText ?? '#718096')
                  : (theme?.secondaryText ?? '#718096'),
          }}>
            <span style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: socialPosterMode === 'production'
                ? '#22c55e'
                : socialPosterMode === 'testing'
                  ? '#f59e0b'
                  : '#9ca3af',
              display: 'inline-block',
            }} />
            {socialPosterMode === null
              ? 'Loading…'
              : socialPosterMode === 'production'
                ? 'Production'
                : socialPosterMode === 'testing'
                  ? 'Testing'
                  : 'Off'}
          </span>
          <TargetSelect
            theme={theme}
            value={socialPosterModeEdit}
            onChange={(e) => { setSocialPosterModeEdit(e.target.value); setSaveModeResult(null); setSaveModeError(null); }}
            aria-label="Social poster mode"
            title="off: does nothing · testing: routes to test Discord only · production: posts to all real channels"
          >
            <option value="off">Off — disabled, no posts</option>
            <option value="testing">Testing — test Discord only, no real APIs</option>
            <option value="production">Production — post to all channels</option>
          </TargetSelect>
          <Button
            type="button"
            className="primary"
            onClick={saveMode}
            disabled={savingMode || socialPosterModeEdit === socialPosterMode}
            theme={theme}
          >
            {savingMode ? 'Saving…' : 'Save'}
          </Button>
          {saveModeResult && !saveModeError && (
            <span style={{ fontSize: '0.85rem', color: '#22c55e' }}>
              Saved — mode is now <strong>{saveModeResult.mode}</strong>
            </span>
          )}
          {saveModeError && (
            <span style={{ fontSize: '0.85rem', color: '#ef4444' }}>{saveModeError}</span>
          )}
        </ActionsRow>
        <p style={{ fontSize: '0.8rem', color: theme?.secondaryText, marginTop: '0.5rem', marginBottom: 0 }}>
          <strong>Off</strong>: Lambda exits immediately, nothing posted.&nbsp;
          <strong>Testing</strong>: sends pick card + all platform previews to your test Discord only.&nbsp;
          <strong>Production</strong>: posts to Discord, X, Instagram, and TikTok.
          The "🧪 Test social poster" button below always routes to test Discord regardless of this setting.
          <br />
          <strong>Local API</strong>: set <code style={{ fontSize: '0.75rem' }}>SOCIAL_POSTER_TEST_LOCAL=1</code> in{' '}
          <code style={{ fontSize: '0.75rem' }}>.env</code> to run the poster in-process (needs AWS creds for S3 + Discord
          test webhook env vars) instead of invoking Lambda — avoids 502 when Lambda is not deployed or reachable.
        </p>
      </CardSection>

      {loading ? (
        <p style={{ color: theme?.secondaryText }}>Loading…</p>
      ) : (
        <>
          <CardSection>
            <SectionHeading theme={theme}>Discord &amp; email</SectionHeading>
            <PageSubtitle theme={theme} style={{ marginBottom: '0.75rem' }}>
              <strong>Manual dispatch</strong> — not the same code path as the automated social poster
              Lambda. Images here are captured in <em>your</em> browser when you attach a card. Edit the
              JSON below if needed, then send. <strong>Picks of the Day</strong> emails can use a
              social-style layout plus SHAP bars; other types follow the Discord template. Subscribers
              vs admins and picks vs results lists apply as selected.
            </PageSubtitle>
            <ActionsRow style={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <DateLabel theme={theme}>Notification</DateLabel>
              <TargetSelect
                theme={theme}
                value={dispatchType}
                onChange={(e) => setDispatchType(e.target.value)}
              >
                {buildDiscordNotificationOptions(picksOfTheDay).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TargetSelect>
              <DateLabel theme={theme}>Email to</DateLabel>
              <TargetSelect
                theme={theme}
                value={emailAudience}
                onChange={(e) => setEmailAudience(e.target.value)}
                aria-label="Email recipient group"
              >
                <option value="subscribers">Subscribers (picks or results list)</option>
                <option value="admins">Admins only</option>
              </TargetSelect>
              <ToggleLabel theme={theme}>
                <input
                  type="checkbox"
                  checked={includeImage}
                  onChange={(e) => setIncludeImage(e.target.checked)}
                />
                Attach selected social card image
              </ToggleLabel>
            </ActionsRow>
            {dispatchType === 'parlay_hit' && parlayList.length > 0 && (
              <ActionsRow style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <DateLabel theme={theme}>Parlay game</DateLabel>
                <TargetSelect
                  theme={theme}
                  value={parlayGameId}
                  onChange={(e) => setParlayGameId(e.target.value)}
                  aria-label="Parlay row for CASHED template and poster test"
                  title="Which parlay row from parlay_of_the_day JSON (by MLB game id)"
                >
                  {parlayList.map((p) => {
                    const gid = String(p?.game_id ?? '');
                    const away = p?.away_team ?? '';
                    const home = p?.home_team ?? '';
                    const label = gid && (away || home) ? `${gid} — ${away} @ ${home}` : gid || 'unknown';
                    return (
                      <option key={gid || label} value={gid}>
                        {label}
                      </option>
                    );
                  })}
                </TargetSelect>
              </ActionsRow>
            )}
            {dispatchType === 'parlay_hit' && parlayList.length === 0 && (
              <p style={{ fontSize: '0.85rem', color: '#ca8a04', margin: '0.25rem 0 0' }}>
                No parlays on S3 for this date — load a slate with a Parlay of the Day first.
              </p>
            )}
            <ActionsRow>
              <Button
                type="button"
                onClick={loadPreview}
                disabled={previewLoading || sendingDiscord !== null || sendingEmail !== null}
                theme={theme}
              >
                {previewLoading ? 'Loading…' : 'Refresh from template'}
              </Button>
              <Button
                type="button"
                className="primary"
                onClick={sendSelectedToDiscord}
                disabled={sendingDiscord !== null || sendingEmail !== null}
                theme={theme}
              >
                {sendingDiscord ? 'Sending…' : 'Send to Discord'}
              </Button>
              <Button
                type="button"
                className="email"
                onClick={sendSelectedToSubscribersEmail}
                disabled={sendingDiscord !== null || sendingEmail !== null || sendingSocialPosterTest}
                theme={theme}
              >
                {sendingEmail
                  ? 'Sending…'
                  : emailAudience === 'admins'
                    ? 'Send email to admins'
                    : 'Send email to subscribers'}
              </Button>
              <Button
                type="button"
                onClick={triggerSocialPosterTest}
                disabled={
                  sendingDiscord !== null ||
                  sendingEmail !== null ||
                  sendingSocialPosterTest ||
                  (dispatchType === 'parlay_hit' && !parlayGameId)
                }
                title="Test mode: pick card + platform previews to test Discord only. With SOCIAL_POSTER_TEST_LOCAL=1, runs in the API process; otherwise invokes AWS Lambda."
                theme={theme}
              >
                {sendingSocialPosterTest ? 'Testing…' : '🧪 Test social poster'}
              </Button>
            </ActionsRow>
            <PreviewTextarea
              theme={theme}
              value={editablePayloadJson}
              onChange={(e) => setEditablePayloadJson(e.target.value)}
              placeholder={previewLoading ? 'Loading…' : 'Select date and notification type to generate payload.'}
              spellCheck={false}
            />
          </CardSection>

          <CardSection>
            <SectionHeading theme={theme}>Picks of the Day</SectionHeading>
            <SectionHint theme={theme}>
              Same React markup as <code>/social-export</code> (renderer screenshots that route for
              automated posts when the S3 PNG exists).
            </SectionHint>
            <ActionsRow>
              <Button
                type="button"
                className="primary"
                onClick={downloadPicks}
                theme={theme}
                title="Browser-only PNG (html2canvas). Not uploaded to S3; not what the poster uses unless you add that step."
              >
                Save PNG (browser)
              </Button>
            </ActionsRow>
            <PicksOfTheDayCard
              dateStr={dateStr}
              data={picksOfTheDay}
              cardRef={picksCardRef}
            />
          </CardSection>

          <CardSection>
            <SectionHeading theme={theme}>Picks of the Day – Model Insights</SectionHeading>
            <SectionHint theme={theme}>
              One card per pick (SHAP radar). <strong>Not</strong> part of the default automated
              daily poster image — manual Discord / email / downloads only unless you extend the
              pipeline.
            </SectionHint>
            {(picksOfTheDay?.picks_of_the_day ?? []).length === 0 ? (
              <CardWrapper>
                <SocialCardInner style={{ minHeight: 120 }}>
                  <EmptyState>No Picks of the Day for this date.</EmptyState>
                </SocialCardInner>
              </CardWrapper>
            ) : (
              (picksOfTheDay.picks_of_the_day || []).map((pick, idx) => {
                const name = pick.player_name ?? pick.playerName ?? 'pick';
                return (
                  <CardSection key={idx} style={{ marginBottom: '1.5rem' }}>
                    <SectionHeading theme={theme} style={{ fontSize: '1rem' }}>
                      {name} – Model Insights
                    </SectionHeading>
                    <ActionsRow>
                      <Button
                        type="button"
                        className="primary"
                        onClick={() => downloadPicksInsight(idx, name)}
                        theme={theme}
                      >
                        Save PNG (browser)
                      </Button>
                    </ActionsRow>
                    <PicksOfTheDayInsightCard
                      dateStr={dateStr}
                      pick={pick}
                      cardRef={(el) => {
                        if (picksInsightsCardRefs.current) {
                          picksInsightsCardRefs.current[idx] = el;
                        }
                      }}
                    />
                  </CardSection>
                );
              })
            )}
          </CardSection>

          <CardSection>
            <SectionHeading theme={theme}>
              {parlayList.length > 1 ? 'Parlays of the Day' : 'Parlay of the Day'}
            </SectionHeading>
            <SectionHint theme={theme}>
              Same components as <code>/social-export</code> for parlay; automated poster uses S3 PNG
              or Pillow as above.
            </SectionHint>
            {parlayList.length === 0 ? (
              <>
                <ActionsRow>
                  <Button type="button" className="primary" disabled theme={theme}>
                    Save PNG (browser)
                  </Button>
                </ActionsRow>
                <ParlayOfTheDayCard
                  dateStr={dateStr}
                  data={null}
                  cardRef={(el) => {
                    primaryParlayCardRef.current = el;
                    parlayCardRefsArray.current[0] = el;
                  }}
                />
              </>
            ) : (
              parlayList.map((parlay, idx) => (
                <React.Fragment key={parlay.game_id}>
                  <ActionsRow>
                    <Button
                      type="button"
                      className="primary"
                      onClick={() => downloadParlayAt(idx)}
                      theme={theme}
                    >
                      Save PNG (browser)
                      {parlayList.length > 1 ? ` (${idx + 1})` : ''}
                    </Button>
                  </ActionsRow>
                  <ParlayOfTheDayCard
                    dateStr={dateStr}
                    data={parlay}
                    cardRef={assignParlayCardRef(idx)}
                  />
                </React.Fragment>
              ))
            )}
          </CardSection>

          <SectionHeading theme={theme} style={{ marginTop: '2rem' }}>
            Results cards (winners)
          </SectionHeading>
          <PageSubtitle theme={theme} style={{ marginBottom: '1rem' }}>
            Same date: show which picks won after games are final.{' '}
            <strong>Manual / subscriber workflows only</strong> — not the default automated POTD /
            parlay image path.
          </PageSubtitle>

          <CardSection>
            <SectionHeading theme={theme}>Picks of the Day – Results</SectionHeading>
            <ActionsRow>
              <Button
                type="button"
                className="primary"
                onClick={downloadPicksResults}
                theme={theme}
              >
                Save PNG (browser)
              </Button>
            </ActionsRow>
            <PicksOfTheDayResultsCard
              dateStr={dateStr}
              data={picksOfTheDay}
              cardRef={picksResultsCardRef}
            />
          </CardSection>

          <CardSection>
            <SectionHeading theme={theme}>
              {parlayList.length > 1
                ? 'Parlays of the Day – Results'
                : 'Parlay of the Day – Results'}
            </SectionHeading>
            {parlayList.length === 0 ? (
              <>
                <ActionsRow>
                  <Button type="button" className="primary" disabled theme={theme}>
                    Save PNG (browser)
                  </Button>
                </ActionsRow>
                <ParlayOfTheDayResultsCard
                  dateStr={dateStr}
                  data={null}
                  cardRef={(el) => {
                    primaryParlayResultsCardRef.current = el;
                    parlayResultsCardRefsArray.current[0] = el;
                  }}
                />
              </>
            ) : (
              parlayList.map((parlay, idx) => (
                <React.Fragment key={`${parlay.game_id}-results`}>
                  <ActionsRow>
                    <Button
                      type="button"
                      className="primary"
                      onClick={() => downloadParlayResultsAt(idx)}
                      theme={theme}
                    >
                      Save PNG (browser)
                      {parlayList.length > 1 ? ` (${idx + 1})` : ''}
                    </Button>
                  </ActionsRow>
                  <ParlayOfTheDayResultsCard
                    dateStr={dateStr}
                    data={parlay}
                    cardRef={assignParlayResultsCardRef(idx)}
                  />
                </React.Fragment>
              ))
            )}
          </CardSection>
        </>
      )}
    </PageContainer>
  );
}

export default AdminSocialCardsPage;
